<script setup lang="ts">
import { ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConsultationsStore } from '@/stores';
import ErrorBanner from '@/components/ErrorBanner.vue';
import FormActions from '@/components/FormActions.vue';
import SectionFormSelector from '@/components/SectionFormSelector.vue';
import FormValueSelector from '@/components/FormValueSelector.vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import type { ConsultationDto, FormDto, Locales, SectionDto, LocalizableString } from '@/services/types/api.types';
import { useLanguageValidationErrors } from '@/composables';
import { onBeforeRouteLeave } from 'vue-router';
import { formSaveNavigationGuard, ensureLocalizableString, resetFormToItem } from '@/helpers/form.helper';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import { localizableString } from '@/helpers/validation.helper';
import { provideEditLocale, getTranslation } from '@/composables/data-display-utils';

const { t } = useI18n();
const props = defineProps<{
  consultationId: number;
  languageEnabled: Locales[];
}>();

const consultationsStore = useConsultationsStore();
const reportLanguage = ref(props.languageEnabled[0]);
const errorReport = ref(false);
const reportPDFGenerationInProgress = ref(false);
const reportPDFLink = ref<string | null>(null);

// Filter state - for building a new filter
const selectedSection = ref<SectionDto | null>(null);
const selectedForm = ref<FormDto | null>(null);
const selectedFilterValue = ref<{
  answerId: number;
  valueId: number | string;
  valueText: LocalizableString;
  stepType: string;
} | null>(null);

// Active filters array
interface ActiveFilter {
  id: string;
  sectionId: number;
  sectionTitle: LocalizableString;
  formId: number;
  formTitle: LocalizableString;
  valueId: number | string;
  valueText: LocalizableString;
}

const activeFilters = ref<ActiveFilter[]>([]);

const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const formContext = useForm({
  validationSchema: yup.object<ConsultationDto>({
    report_summary: localizableString(props.languageEnabled),
  }),
});
const { handleSubmit, errors, meta, resetForm } = formContext;
const languageErrors = useLanguageValidationErrors(errors);

onBeforeRouteLeave(formSaveNavigationGuard(meta));
const consultation = useWatchItemLoading<ConsultationDto>(toRef(props, 'consultationId'), consultationsStore, (f) => {
  ensureLocalizableString(f, 'report_summary', props.languageEnabled);
  resetFormToItem(formContext, f);
});

const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  wrapSavingOperation(
    async () => await consultationsStore.save(values as ConsultationDto),
    () => resetForm({ values: consultation.value! }),
  );
});

const onCancel = () => {
  if (consultation.value) {
    resetForm({ values: consultation.value });
  } else {
    console.warn('onCancel: consultation is null');
  }
};

// Filter handlers
const onSectionSelected = (section: SectionDto) => {
  selectedSection.value = section;
  selectedForm.value = null;
  selectedFilterValue.value = null; // Reset filter when section changes
};

const onFormSelected = (form: FormDto) => {
  selectedForm.value = form;
  selectedFilterValue.value = null; // Reset filter when form changes
};

const onValueSelected = (
  value: { answerId: number; valueId: number | string; valueText: LocalizableString; stepType: string } | null,
) => {
  selectedFilterValue.value = value;
};

const onSectionCleared = () => {
  selectedSection.value = null;
  selectedForm.value = null;
  selectedFilterValue.value = null;
};

const onFormCleared = () => {
  selectedForm.value = null;
  selectedFilterValue.value = null;
};

// Add the current filter to active filters
const addFilter = () => {
  if (!selectedForm.value || !selectedFilterValue.value || !selectedSection.value) return;

  const newFilter: ActiveFilter = {
    id: `${selectedForm.value.id}-${selectedFilterValue.value.valueId}-${Date.now()}`,
    sectionId: selectedSection.value.id!,
    sectionTitle: selectedSection.value.title || ({} as LocalizableString),
    formId: selectedForm.value.id,
    formTitle: selectedForm.value.question?.title || ({} as LocalizableString),
    valueId: selectedFilterValue.value.valueId,
    valueText: selectedFilterValue.value.valueText,
  };

  activeFilters.value.push(newFilter);

  // Reset the form selectors after adding - clear both components properly
  selectedSection.value = null;
  selectedForm.value = null;
  selectedFilterValue.value = null;
};

// Remove a specific filter
const removeFilter = (filterId: string) => {
  activeFilters.value = activeFilters.value.filter((f) => f.id !== filterId);
};

// Clear all filters
const clearAllFilters = () => {
  activeFilters.value = [];
  selectedSection.value = null;
  selectedForm.value = null;
  selectedFilterValue.value = null;
};

const exportPDF = async () => {
  errorReport.value = false;
  reportPDFLink.value = null;
  reportPDFGenerationInProgress.value = true;

  // Prepare filter parameters from active filters
  const filterParams =
    activeFilters.value.length > 0
      ? activeFilters.value.map((filter) => ({
          formId: filter.formId,
          valueId: filter.valueId,
        }))
      : null;

  console.log('Filter parameters:', filterParams);

  const fileResponse = await consultationsStore.getPDFReport(props.consultationId, reportLanguage.value, filterParams);
  if (
    fileResponse?.success &&
    'data' in fileResponse &&
    typeof fileResponse.data === 'object' &&
    fileResponse.data !== null &&
    'url' in fileResponse.data &&
    'fileName' in fileResponse.data &&
    typeof fileResponse.data.url === 'string' &&
    typeof fileResponse.data.fileName === 'string'
  ) {
    reportPDFLink.value = fileResponse.data.url.replace('reporting:5488', 'localhost:5488');
    if (reportPDFLink.value) {
      await downloadReport(reportPDFLink.value, fileResponse.data.fileName);
    }
  } else {
    errorReport.value = true;
  }
  reportPDFGenerationInProgress.value = false;
};

const downloadReport = async (url: string, fileName: string) => {
  try {
    console.log(url);
    // Fetch the PDF file
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const blob = await response.blob();

    // Create a Blob URL
    const blobUrl = URL.createObjectURL(blob);

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;

    // Append the link to the body (required for Firefox)
    document.body.appendChild(link);

    // Trigger the download by simulating a click
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading PDF file:', error);
  }
};
</script>

<template>
  <div class="column">
    <div class="summary q-mb-md">
      <form-actions
        v-model:editLocale="editLocale"
        :onCancel="onCancel"
        :onSubmit="onSubmit"
        :languageErrors="languageErrors"
        :meta="meta"
        :show-language-switcher="true"
        :language-enabled="props.languageEnabled"
      />
      <q-field :label="t('global.label.report_summary')" stack-label>
        <template v-slot:control>
          <localized-editor
            name="report_summary"
            :label="t('global.label.report_summary')"
            class="q-mt-sm full-width"
            :editLocale="editLocale"
            :language_enabled="props.languageEnabled"
            disable-sanitize-html
          />
        </template>
      </q-field>
    </div>
    <q-separator :spaced="true" />
    <div class="q-mt-md">
      <h3>Export</h3>
      <div class="actions q-mt-md">
        <q-select
          class="btn"
          v-model="reportLanguage"
          :options="props.languageEnabled"
          :label="t('consultation.export.language')"
        />

        <q-btn
          color="primary btn"
          :label="t('consultation.export.pdf')"
          @click="exportPDF"
          :loading="reportPDFGenerationInProgress"
        />

        <p v-if="reportPDFGenerationInProgress">{{ t('consultation.export.pdfInProgress') }}</p>
        <p class="q-mt-sm">
          <a v-if="!reportPDFGenerationInProgress && reportPDFLink" :href="reportPDFLink" target="_blank">{{
            t('consultation.export.pdfDownload')
          }}</a>
        </p>
        <error-banner :isError="errorReport" :error="t('consultation.export.errorDownload')" />
      </div>
    </div>
    <q-separator :spaced="true" />
    <!-- Filter Section -->
    <div class="filter-section full-width q-mt-md q-mb-md">
      <div class="filter-section__header">
        <h3>{{ t('consultation.export.filter.title') }}</h3>
        <q-btn
          v-if="activeFilters.length > 0"
          flat
          dense
          color="negative"
          icon="clear_all"
          :label="t('consultation.export.filter.clearAll')"
          @click="clearAllFilters"
          class="clear-all-btn"
        />
      </div>

      <!-- Active filters display -->
      <div v-if="activeFilters.length > 0" class="active-filters q-mt-md q-mb-md">
        <div class="active-filters__label">{{ t('consultation.export.filter.activeFilters') }}:</div>
        <div class="active-filters__chips">
          <q-chip
            v-for="filter in activeFilters"
            :key="filter.id"
            removable
            @remove="removeFilter(filter.id)"
            color="primary"
            text-color="white"
            icon="filter_alt"
          >
            <span class="filter-chip-content">
              <strong>{{ getTranslation(filter, 'formTitle', props.languageEnabled) }}</strong>
              :
              {{ getTranslation(filter, 'valueText', props.languageEnabled) }}
            </span>
          </q-chip>
        </div>
      </div>

      <!-- Filter builder -->
      <div class="filter-controls q-mt-md">
        <div class="filter-controls__label">{{ t('consultation.export.filter.addFilter') }}:</div>
        <section-form-selector
          :consultation-id="props.consultationId"
          :language-enabled="props.languageEnabled"
          :section="selectedSection"
          :form="selectedForm"
          @section-selected="onSectionSelected"
          @section-cleared="onSectionCleared"
          @form-selected="onFormSelected"
          @form-cleared="onFormCleared"
        />

        <form-value-selector
          v-if="selectedForm"
          :selected-form="selectedForm"
          :language-enabled="props.languageEnabled"
          @value-selected="onValueSelected"
          class="q-mt-md"
        />

        <q-btn
          v-if="selectedForm && selectedFilterValue"
          color="positive"
          icon="add"
          :label="t('consultation.export.filter.addFilterButton')"
          @click="addFilter"
          class="q-mt-md"
          unelevated
        />
      </div>
    </div>

    <q-separator :spaced="true" />
  </div>
</template>

<style scoped lang="scss">
.actions {
  display: flex;
  flex-direction: row;
  row-gap: 10px;
  gap: 10px;

  .btn {
    width: 25%;
  }
}

.filter-section {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    h3 {
      margin: 0;
    }

    .clear-all-btn {
      margin-left: auto;
    }
  }
}

.active-filters {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #e0e0e0;

  &__label {
    font-weight: 500;
    margin-bottom: 10px;
    color: #333;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .filter-chip-content {
    display: flex;
    gap: 5px;
    align-items: center;
    max-width: 400px;
    overflow: hidden;

    strong {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 200px;
    }

    // The value text after the colon
    & > :last-child {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
      min-width: 0;
    }
  }
}

.filter-controls {
  &__label {
    font-weight: 500;
    margin-bottom: 10px;
    color: #555;
  }
}
</style>
